// lib/services/compute/color_gpu_pool.ts

import TypedSlotPool, { PostReleasePolicy } from "../lib/services/compute/typed_slot_pool";
import ColorGpuSlot from "./color_gpu_slot";

const SLOT_COUNT = 3;

/**
 * Three-slot pool of per-frame compute workspaces for sticker detection.
 *
 * Producer: StickerCompute
 *   - acquires an Idle slot
 *   - rebuilds pipeline bind groups against the slot's buffers/textures
 *   - dispatches all compute passes
 *   - publishes the slot (results ready for readback)
 *
 * Consumer: StickerCpuPool (readback)
 *   - leases the latest Ready slot
 *   - mapAsyncs the sticker_buffer
 *   - releases when readback is complete
 *
 * The pool uses PostReleasePolicy.KeepReady so the readback consumer can
 * re-read the same results if no newer compute has finished.
 *
 * The 15 compute pipelines are shared across all slots. Only the bind
 * groups get rebuilt per-slot (via each pipeline's rebuild() method).
 */
export default class ColorGpuPool {
  private pool: TypedSlotPool<ColorGpuSlot>;
  private slots: ColorGpuSlot[] = [];
  private initialized: boolean = false;
  private sequence_counter: number = 0;

  private width: number = 0;
  private height: number = 0;

  constructor() {
    this.pool = new TypedSlotPool<ColorGpuSlot>(PostReleasePolicy.KeepReady);
  }

  /**
   * Allocate three slots, each with its own set of GPU buffers and
   * textures sized to the camera frame dimensions.
   *
   * Called once when the first camera frame arrives and we know the
   * actual resolution. If the resolution changes (unlikely but
   * defensive), shutdown and re-initialize.
   */
  initialize(
    device: GPUDevice,
    key: string,
    width: number,
    height: number,
    nngrid_size: number,
    sticker_buffer_size: number,
    blob_buffer_size: number
  ): boolean {
    if (this.initialized) return true;
    if (width <= 0 || height <= 0) return false;

    this.width = width;
    this.height = height;

    this.slots = [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = new ColorGpuSlot();
      slot.allocate_resources(
        device,
        key,
        i,
        width,
        height,
        nngrid_size,
        sticker_buffer_size,
        blob_buffer_size
      );
      this.slots.push(slot);
    }

    this.pool.initialize(this.slots);
    this.initialized = true;
    return true;
  }

  shutdown(): void {
    for (const slot of this.slots) {
      slot.destroy_resources();
    }
    this.pool.shutdown();
    this.slots = [];
    this.width = 0;
    this.height = 0;
    this.sequence_counter = 0;
    this.initialized = false;
  }

  is_initialized(): boolean { return this.initialized; }

  // ---- Producer-side API ------------------------------------------------

  acquire_idle_slot(): ColorGpuSlot | null {
    return this.pool.acquire_idle_slot();
  }

  publish_slot(slot: ColorGpuSlot, timestamp_sec: number): void {
    this.sequence_counter += 1;
    this.pool.publish_slot(slot, this.sequence_counter, timestamp_sec);
  }

  cancel_slot(slot: ColorGpuSlot): void {
    slot.cancel_record();
    this.pool.cancel_slot(slot);
  }

  // ---- Consumer-side API ------------------------------------------------

  acquire_latest_ready_slot(): ColorGpuSlot | null {
    return this.pool.acquire_latest_ready_slot();
  }

  release_latest_ready_slot(): void {
    this.pool.release_leased_slot();
  }

  /**
   * Non-leasing read for render-side consumers that need to display
   * the debug texture without participating in the lease chain.
   */
  peek_latest_ready_slot(): ColorGpuSlot | null {
    return this.pool.peek_latest_ready_slot();
  }

  // ---- Introspection ----------------------------------------------------

  get_width(): number { return this.width; }
  get_height(): number { return this.height; }
  get_slot_count(): number { return this.pool.getSlotCount(); }
  get_idle_count(): number { return this.pool.getIdleCount(); }
  get_latest_published_sequence(): number { return this.pool.getLatestPublishedSequence(); }
  get_latest_consumed_sequence(): number { return this.pool.getLatestConsumedSequence(); }
  get_superseded_count(): number { return this.pool.getSupersededCount(); }
  has_published_slot(): boolean { return this.pool.getLatestPublishedSequence() > 0; }
} 